import DataLayer from '@services/data-layer/index'
import { getCookie, deleteCookie } from '@utils/cookies'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import analyticsService from './analytics-service'
import settingsService from './settings-service'
import consentService from './consent-service'
import { onDemandQualityOptions } from '../configuration'

const CHECK_VERSION_INTERVAL_DURATION = 1000 * 60 * 60 // 1 hour

class AppInitializer {
    constructor({ store, router, messages, features = {} }) {
        this.store = store
        this.router = router
        this.messages = messages
        this.checkVersionInterval = null

        this.features = {
            auth: true,
            channels: true,
            genres: true,
            versionCheck: true,
            premium: true,
            settings: true,
            favouritesOnboarding: true,
            premiumOnboarding: true,
            ...features,
        }
    }

    isFeatureEnabled(key) {
        return !!this.features[key]
    }

    enableFeature(key) {
        this.features[key] = true
    }

    disableFeature(key) {
        this.features[key] = false

        if (key === 'versionCheck' && this.checkVersionInterval) {
            clearInterval(this.checkVersionInterval)
            this.checkVersionInterval = null
        }
    }

    setFeatures(newFeatures = {}) {
        this.features = Object.assign(this.features, newFeatures)
    }

    get isLoggedIn() {
        return this.store.getters['auth/loggedIn']
    }

    get isPremium() {
        return this.store.getters['premium/isPremium']
    }

    get currentUser() {
        return this.store.state.auth.currentUser?.members_id
    }

    async initialize() {
        if (this.isFeatureEnabled('auth')) this.checkAuthentication()
        if (this.isFeatureEnabled('channels')) this.initChannels()
        if (this.isFeatureEnabled('genres')) this.initGenres()
        if (this.isFeatureEnabled('versionCheck')) this.startCodeVersionCheck()
        if (this.isFeatureEnabled('premium')) await this.initPremium()
        if (this.isFeatureEnabled('settings')) await this.initSettings()
        if (this.isFeatureEnabled('favouritesOnboarding')) return this.initOnboarding()
        if (this.isFeatureEnabled('premiumOnboarding')) await this.handlePremiumOnboarding()
    }

    // Authentication Methods
    checkAuthentication() {
        if (!this.isFeatureEnabled('auth')) return

        const oauthSession = getCookie('oauthSession') || ''
        if (oauthSession) {
            const user = JSON.parse(oauthSession)
            this.store.dispatch('auth/setUser', user)
            this.store.dispatch('member/init', null, { root: true })
            deleteCookie({ name: 'oauthSession', domain: import.meta.env.VITE_COOKIES_DOMAIN })

            // Grant consent for OAuth logins (user agreed to Privacy Policy by using social auth)
            consentService.grantConsent('oauth')

            postAuthRedirect.resolveAfterAuth(localSave.get('redirectURL') || '/')
        }
    }

    // Channel Initialization
    initChannels() {
        if (!this.isFeatureEnabled('channels')) return

        DataLayer.channels.get().then(response => {
            const channels = Object.values(response.body).sort((a, b) =>
                a.title > b.title ? 1 : b.title > a.title ? -1 : 0
            )
            this.store.commit('channels/SET_CHANNELS', channels)
        })
    }

    // Genre Initialization
    async initGenres() {
        if (!this.isFeatureEnabled('genres')) return

        const genresRes = await DataLayer.genres.getAll()

        const genres = genresRes ? genresRes.body : []

        this.store.commit('genres/SET_GENRES', genres)
    }

    // Code Version Checking
    startCodeVersionCheck() {
        if (!this.isFeatureEnabled('versionCheck')) return

        this.checkCodeVersion()
        this.checkVersionInterval = setInterval(() => {
            this.checkCodeVersion()
        }, CHECK_VERSION_INTERVAL_DURATION)
    }

    async checkCodeVersion() {
        if (!this.isFeatureEnabled('versionCheck')) return

        const localCodeVersion = localSave.get('localCodeVersion')
        try {
            const response = await DataLayer.versions.get()
            const actualCodeVersion = response.body.clientVersion
            localSave.save('actualCodeVersion', actualCodeVersion)

            if (!localCodeVersion) localSave.save('localCodeVersion', actualCodeVersion)
            else if (localCodeVersion !== actualCodeVersion) {
                this.notifyForNewVersion(localCodeVersion, response.body)
            }
        } catch (error) {
            console.error('Failed to check code version:', error)
        }
    }

    notifyForNewVersion(oldVersion, newVersion) {
        const messageKey = newVersion.severity === 'critical' ? 'CRITICAL_UPDATE_AVAILABLE' : 'UPDATE_AVAILABLE'

        this.store.dispatch('appearance/handleNotification', {
            message: this.messages(messageKey),
            type: 'warning',
            hasReload: true,
            isPersistentNotification: true,
            forceUpdate: newVersion.severity === 'critical',
        })
    }

    async handlePremiumLoad() {
        if (!this.isFeatureEnabled('premium')) return
        if (!this.isLoggedIn) return
        if (!localSave.get('hasAppLoaded') && !this.isPremium) {
            localSave.save('hasAppLoaded', true)
            if (window.location.pathname.includes('premium')) return
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            this.router.push({ path: '/premium' })
        }
    }

    // Premium Initialization
    async initPremium() {
        if (!this.isFeatureEnabled('premium')) return

        const premiumService = DataLayer.subscriptions
        const promises = [premiumService.getProducts()]
        const memberId = this.currentUser?.id
        if (this.isLoggedIn) promises.push(premiumService.getSubscriptionsByMemberId(memberId))
        const [plans, subscriptions = null] = await Promise.all(promises)
        this.store.commit('premium/SET_PLANS', plans.body)
        if (subscriptions) {
            this.store.commit('premium/SET_SUBSCRIPTIONS', subscriptions.body)
            const activeSubscription = this.store.getters['premium/activeSubscription']
            if (activeSubscription?.subscriptionsCheckoutId?.id) {
                const transaction = await premiumService.getCheckoutById(activeSubscription.subscriptionsCheckoutId.id)
                this.store.commit('premium/SET_ACTIVE_TRANSACTION', transaction.body)
            }
        }
        this.store.commit('premium/SET_IS_LOADING', false)
        await this.handlePremiumLoad()
    }

    async handlePremiumOnboarding() {
        if (!this.isFeatureEnabled('premiumOnboarding')) return
        if (!this.isLoggedIn || localSave.get('hasSkippedPremiumOnboarding') || this.isPremium) return

        if (!this.router.currentRoute.name.includes('premium')) {
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            await this.router.push({ path: '/premium' })
        }
    }

    // Settings Initialization
    async initSettings() {
        if (!this.isFeatureEnabled('settings')) return
        if (!this.isLoggedIn) return

        const savedQuality = await settingsService.getCurrentOnDemandQualityKey()
        const defaultQuality = this.isPremium ? 'high' : 'low'
        let onDemandQuality = savedQuality || defaultQuality

        // Validate that saved quality matches premium entitlement
        if (savedQuality) {
            const savedQualityOption = onDemandQualityOptions[savedQuality]

            // If user has a premium quality saved but is no longer premium, reset to default
            if (savedQualityOption?.needsSub && !this.isPremium) {
                onDemandQuality = defaultQuality
                await settingsService.setCurrentOnDemandQuality(onDemandQuality)
            }
        } else {
            // No saved preference, set default
            await settingsService.setCurrentOnDemandQuality(onDemandQuality)
        }

        this.store.commit('settings/SET_SETTINGS', { currentOnDemandQualityKey: onDemandQuality })
    }

    // Favorites onboarding
    async initFavorites() {
        if (!this.currentUser) return null

        await Promise.all([
            this.store.dispatch('favorites/fetchType', 'artists'),
            this.store.dispatch('favorites/fetchType', 'genres'),
        ])

        return {
            artists: this.store.state.favorites.artistsList,
            genres: this.store.state.favorites.genresList,
        }
    }

    async initOnboarding() {
        if (!this.isFeatureEnabled('favouritesOnboarding')) return false
        if (!this.currentUser) return false

        const MINIMUM_COUNT = 3
        const ONBOARDING_PATH = 'onboarding'
        const favorites = await this.initFavorites()
        if (!favorites) return false

        const { artists, genres } = favorites
        const shouldOnboard = artists.length < MINIMUM_COUNT && genres.length < MINIMUM_COUNT
        const isOnboarding = this.router.currentRoute.name === ONBOARDING_PATH

        if (!shouldOnboard) return false
        else {
            if (isOnboarding) return true
            await this.router.push({ name: ONBOARDING_PATH })
            return true
        }
    }

    // Cleanup
    destroy() {
        if (this.checkVersionInterval) clearInterval(this.checkVersionInterval)
    }
}

export default AppInitializer
